/* eslint-disable react-hooks/exhaustive-deps */
import List from "@mui/material/List"
import Input from "@mui/material/Input"
import Avatar from "@mui/material/Avatar"
import { BsUiChecks } from "react-icons/bs"
import * as api from '../../../services/api'
import ListItem from "@mui/material/ListItem"
import { useNavigate } from "react-router-dom"
import { useCore } from "../../../hooks/useCore"
import { useAuth } from "../../../hooks/useAuth"
import InputLabel from "@mui/material/InputLabel"
import SearchIcon from '@mui/icons-material/Search'
import FormControl from "@mui/material/FormControl"
import { CompanyProps } from "../../../types/company"
import ListItemText from "@mui/material/ListItemText"
import { StatusColor } from "../../../styles/lightMode"
import useWindowSize from "../../../hooks/useWindowSize"
import ListItemButton from "@mui/material/ListItemButton"
import InputAdornment from "@mui/material/InputAdornment"
import ListItemAvatar from "@mui/material/ListItemAvatar"
import { Paginator } from "../../../components/Paginator"
import { ListQueryProps, QueryProps, RetrieveQueryProps, ResponseQueryProps } from "../../../types/query"
import InputDatePicker from "../../../components/InputDatePicker"
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch'
import ResponseButton from "../../../components/ResponseButton"
import React, { useCallback, useEffect, useLayoutEffect, useState, useRef } from "react"
import { Alert, Box, Button, Chip, colors, Grid, LinearProgress, MenuItem, Select, SelectChangeEvent, Tooltip, Typography } from "@mui/material"
import { toast } from "react-toastify"
import CircularProgress from "@mui/material/CircularProgress"
import { useDebounce } from "../../../utils/useDebounce";
import { getResponseContent } from "../../../utils/apiResponse";

type CsvExportStatus = "pending" | "processing" | "completed" | "error";
type CsvExportPhase = "idle" | "starting" | CsvExportStatus | "downloading";

type CsvExportPayload = {
  id: string;
  status: CsvExportStatus;
  progress_percent: number;
  processed_rows: number;
  total_rows: number;
  error_message: string | null;
  status_url: string;
  download_url: string | null;
}

type CsvExportState = CsvExportPayload & {
  phase: CsvExportPhase;
  fileName: string;
}

const EMPTY_CSV_EXPORT: CsvExportState = {
  id: "",
  status: "pending",
  phase: "idle",
  progress_percent: 0,
  processed_rows: 0,
  total_rows: 0,
  error_message: null,
  status_url: "",
  download_url: null,
  fileName: "",
}

const CSV_EXPORT_POLL_INTERVAL_MS = Number(process.env.REACT_APP_CSV_EXPORT_POLL_INTERVAL_MS ?? 3000);

function getApiMessage(response: any, fallback: string) {
  const content = getResponseContent<any>(response);
  return content?.message ?? content?.detail ?? content?.error_message ?? response?.data?.detail ?? fallback;
}

function statusToPhase(status: CsvExportStatus): CsvExportPhase {
  if (status === "completed" || status === "error") return status;
  return status === "processing" ? "processing" : "pending";
}

function formatNumber(value?: number | null) {
  return Number(value ?? 0).toLocaleString("pt-BR");
}

// call/list/ nem sempre traz `responses` populado por item — cai pra
// call/retrieve/{id}/ (que sempre traz) só quando necessário.
async function resolveResponsesForQuery(query: QueryProps | null): Promise<ResponseQueryProps[]> {
  if (!query) return []
  if (query.responses?.length) return query.responses

  try {
    const detail = (await api.get(`call/retrieve/${query.id}/`)) as RetrieveQueryProps
    return detail.content.responses ?? []
  } catch {
    return []
  }
}

export default function Queries(): JSX.Element {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { width } = useWindowSize()
  const {
    setQueries,
    setTitleBar,
    countQueries,
    queriesSearch,
    setCountQueries,
    setPathTitleBar,
    finalDateQueries,
    setQueriesSearch,
    companyIdQueries,
    pageNumberQueries,
    initialDateQueries,
    setFinalDateQueries,
    setCompanyIdQueries,
    setPageNumberQueries,
    setInitialDateQueries,
  } = useCore() 
  const isAdmin = user?.profile === "3"
  const [companies, setCompanies] = useState<CompanyProps[] | undefined>([])
  // const [xlsData, setXlsData] = useState<object>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [csvExport, setCsvExport] = useState<CsvExportState>(EMPTY_CSV_EXPORT);

  const [resultsVisualizations, setResultsVisualizations] = useState(0)
  const qtdVisualizations = [20, 50 , 100]
  
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearch = useDebounce(searchValue, 500);
  const lastSearchedRef = useRef(debouncedSearch);

  const POLL_INTERVAL_MS = Number(process.env.REACT_APP_QUERY_HISTORY_PAGE_POLL_INTERVAL_MS ?? 30000);
  const POLL_MAX_TRIES = Number(process.env.REACT_APP_QUERY_HISTORY_PAGE_POLL_MAX_TRIES ?? 30);

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollAttemptsRef = useRef<number>(0);
  const csvExportPollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const csvExportResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoDownloadedExportRef = useRef<string | null>(null);
  const csvExportFileNameRef = useRef<string>("");

  const hasRunningActivity = useCallback(
    () => (queriesSearch ?? []).some(q => q.get_status_display === "Atividade em execução"),
    [queriesSearch]
  );

  // Para evitar múltiplas inicializações
  const didBootstrapRef = useRef<boolean>(false);


  const getQueries = async (page?: number, companyIdOverride?: string) => {
    //setLoading(true)

    const companyId = companyIdOverride ?? companyIdQueries

    // Validação do intervalo de datas
    if (initialDateQueries && finalDateQueries) {

      const diffMonths = finalDateQueries.diff(initialDateQueries, 'months')

      const maxMonths = companyId === "all" ? 5 : 12

      if (diffMonths > maxMonths) {
        setLoading(false)
        toast.error(`Para consultas ${companyId === "all" ? "com todas as empresas" : "de histórico"}, o intervalo máximo permitido é de ${maxMonths} meses.`)
        return
      }
    }
    let url = `call/list/?initial_date=${initialDateQueries?.toISOString().split('T')[0]}&final_date=${finalDateQueries?.toISOString().split('T')[0]}`

    if (companyId !== "0") {
      url = `${url}&id=${companyId}`
    }

    if (page) {
      url = `${url}&page=${page}`
    }

    if (resultsVisualizations) {
      url = `${url}&page_size=${resultsVisualizations}`
    }

    const term = (debouncedSearch || '').trim();
    if (term.length >= 2) {
      url = `${url}&search=${encodeURIComponent(term)}`;
    }

    const response = await api.get(url) as ListQueryProps

    setQueries(response.content.results)
    setQueriesSearch(response.content.results)
    setCountQueries(response.content.count)
    setLoading(false)
  }

  const getCompanies = useCallback(async () => {
    await api.get('company/list/all/?minimal=true').then((response: any) => {
      setCompanies(response.content)
    })
  }, [])

  const handleChangePage = async (_event: React.ChangeEvent<unknown>, value: number) => {
    setLoading(true)
    setPageNumberQueries(value)
    await getQueries(value)
  }

  const handleCompanyChange = (event: SelectChangeEvent) => {
    setCompanyIdQueries(event.target.value)
  }

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  }

  const handleResultsChange = (event: SelectChangeEvent<Number>) => {
    setResultsVisualizations(Number(event.target.value))
  }

  const isCsvExportBusy = ["starting", "pending", "processing", "downloading"].includes(csvExport.phase);
  const showCsvExportProgress = csvExport.phase !== "idle";
  const canDownloadCsvExport = csvExport.phase === "completed" && !!csvExport.download_url;

  function clearCsvExportTimers() {
    if (csvExportPollTimerRef.current) {
      clearInterval(csvExportPollTimerRef.current);
      csvExportPollTimerRef.current = null;
    }
    if (csvExportResetTimerRef.current) {
      clearTimeout(csvExportResetTimerRef.current);
      csvExportResetTimerRef.current = null;
    }
  }

  function getCsvExportRequest() {
    if (!initialDateQueries || !finalDateQueries) {
      toast.error("Informe a data inicial e final para extrair o CSV.", {
        position: toast.POSITION.TOP_RIGHT
      });
      return null;
    }

    if (isAdmin && companyIdQueries === "0") {
      toast.error("Selecione uma empresa ou a opção Todas as empresas.", {
        position: toast.POSITION.TOP_RIGHT
      });
      return null;
    }

    const companyId = isAdmin ? companyIdQueries : user?.corporate_id;
    if (!companyId) {
      toast.error("Não foi possível identificar a empresa para extração.", {
        position: toast.POSITION.TOP_RIGHT
      });
      return null;
    }

    const initialDate = initialDateQueries.toISOString().split("T")[0];
    const finalDate = finalDateQueries.toISOString().split("T")[0];
    const company = companies?.find((value) => value.id === companyId);
    const companyName = companyId === "all" ? "Geral" : company?.corporate_name ?? companyId;

    return {
      companyId,
      payload: {
        initial_date: initialDate,
        final_date: finalDate,
      },
      fileName: `Relatorio_${companyName}_${initialDate}_${finalDate}.csv`,
    }
  }

  function applyCsvExportPayload(payload: CsvExportPayload, fileName?: string) {
    setCsvExport((previous) => ({
      ...previous,
      ...payload,
      phase: statusToPhase(payload.status),
      fileName: fileName ?? previous.fileName,
      progress_percent: Math.min(Math.max(payload.progress_percent ?? 0, 0), 100),
      processed_rows: payload.processed_rows ?? 0,
      total_rows: payload.total_rows ?? 0,
      error_message: payload.error_message ?? null,
      download_url: payload.download_url ?? null,
    }));
  }

  function startCsvExportPolling(exportId: string) {
    if (csvExportPollTimerRef.current) return;

    csvExportPollTimerRef.current = setInterval(() => {
      refreshCsvExportStatus(exportId).catch((error) => {
        console.error("CSV export polling error", error);
        clearCsvExportTimers();
        setCsvExport((previous) => ({
          ...previous,
          phase: "error",
          status: "error",
          error_message: "Erro ao consultar o status da planilha.",
        }));
      });
    }, CSV_EXPORT_POLL_INTERVAL_MS);
  }

  async function startCsvExport() {
    const request = getCsvExportRequest();
    if (!request) return;

    clearCsvExportTimers();
    autoDownloadedExportRef.current = null;
    csvExportFileNameRef.current = request.fileName;
    setCsvExport({
      ...EMPTY_CSV_EXPORT,
      phase: "starting",
      fileName: request.fileName,
    });

    try {
      const response = await api.post(
        `company/download/invoice/${request.companyId}/start/`,
        request.payload
      ) as any;

      if (response?.status >= 400) {
        throw new Error(getApiMessage(response, "Erro ao iniciar a geração da planilha."));
      }

      const payload = getResponseContent<CsvExportPayload>(response);
      if (!payload?.id) {
        throw new Error("A API não retornou o identificador da exportação.");
      }

      applyCsvExportPayload(payload, request.fileName);

      if (payload.status === "completed" && payload.download_url) {
        await downloadCsvExport(payload, request.fileName, true);
        return;
      }

      if (payload.status === "error") {
        throw new Error(payload.error_message ?? "Erro ao gerar a planilha.");
      }

      startCsvExportPolling(payload.id);
    } catch (error: any) {
      clearCsvExportTimers();
      setCsvExport((previous) => ({
        ...previous,
        phase: "error",
        status: "error",
        error_message: error?.message ?? "Erro ao iniciar a geração da planilha.",
      }));
      toast.error(error?.message ?? "Erro ao iniciar a geração da planilha.", {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  };

  async function refreshCsvExportStatus(exportId: string) {
    const response = await api.get(`company/download/invoice/export/${exportId}/status/`) as any;
    const payload = getResponseContent<CsvExportPayload>(response);

    if (!payload?.id) {
      throw new Error(getApiMessage(response, "Status da exportação não encontrado."));
    }

    applyCsvExportPayload(payload);

    if (payload.status === "completed" && payload.download_url) {
      clearCsvExportTimers();

      if (autoDownloadedExportRef.current !== payload.id) {
        autoDownloadedExportRef.current = payload.id;
        await downloadCsvExport(payload, csvExportFileNameRef.current || csvExport.fileName, true);
      }
      return;
    }

    if (payload.status === "error") {
      clearCsvExportTimers();
      toast.error(payload.error_message ?? "Erro ao gerar a planilha.", {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  }

  async function downloadCsvExport(
    payload: CsvExportPayload | CsvExportState = csvExport,
    fileName = csvExport.fileName,
    automatic = false
  ) {
    if (!payload.id) return;

    try {
      setCsvExport((previous) => ({
        ...previous,
        phase: "downloading",
      }));

      const response = await api.get(`company/download/invoice/export/${payload.id}/file/`) as any;
      const downloadUrl = getResponseContent<{ download_url?: string }>(response)?.download_url;
      if (!downloadUrl) {
        throw new Error(getApiMessage(response, "A API não retornou uma URL de download válida."));
      }

      window.location.href = downloadUrl;
      setCsvExport((previous) => ({
        ...previous,
        phase: "completed",
        status: "completed",
        progress_percent: 100,
      }));

      if (automatic) {
        csvExportResetTimerRef.current = setTimeout(() => {
          setCsvExport(EMPTY_CSV_EXPORT);
        }, 8000);
      }
    } catch (error: any) {
      setCsvExport((previous) => ({
        ...previous,
        phase: "completed",
        status: "completed",
        error_message: null,
      }));
      toast.error(error?.message ?? "Erro ao baixar a planilha.", {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  }

  function getCsvExportStatusText() {
    if (csvExport.phase === "starting") return "Solicitando geração da planilha...";
    if (csvExport.phase === "pending") return "Planilha na fila de geração...";
    if (csvExport.phase === "processing") return "Gerando planilha...";
    if (csvExport.phase === "downloading") return "Planilha pronta. Iniciando download...";
    if (csvExport.phase === "completed") return "Planilha pronta para download.";
    if (csvExport.phase === "error") return csvExport.error_message ?? "Erro ao gerar a planilha.";
    return "";
  }


  useLayoutEffect(() => {
    setPathTitleBar("/")
    setTitleBar("Minhas consultas")
  }, [])


  useEffect(() => {
    getCompanies();

    if (didBootstrapRef.current) return;
    didBootstrapRef.current = true;

    const bootstrap = async () => {
      if (user?.corporate_id) {
        const companyId = String(user.corporate_id);
        setCompanyIdQueries(companyId);
        await getQueries(undefined, companyId).catch(console.error);
        return;
      }

      if (queriesSearch!.length > 0) {
        setLoading(false);
      } else {
        await getQueries().catch(console.error);
      }
    };

    bootstrap().catch(console.error);

    // Cleanup: garante que o polling seja limpo ao desmontar a página
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      clearCsvExportTimers();
    };
  }, []);


  useEffect(() => {
    if (lastSearchedRef.current === debouncedSearch) return;
    lastSearchedRef.current = debouncedSearch;

    const term = debouncedSearch.trim();
    if (term.length === 0) {
      setPageNumberQueries(1);
      getQueries().catch(console.error);
    } else if (term.length >= 2) {
      setPageNumberQueries(1);
      getQueries().catch(console.error);
    }
  }, [debouncedSearch]);


  // Polling automático enquanto houver "Atividade em execução"
  useEffect(() => {
    if (pollTimerRef.current && !hasRunningActivity()) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      pollAttemptsRef.current = 0;
    }

    // Se NÃO há item em execução, não inicia polling.
    if (!hasRunningActivity()) return;

    // Se já está rodando um timer, evita duplicar.
    if (pollTimerRef.current) return;

    // Inicia polling
    pollAttemptsRef.current = 0;
    pollTimerRef.current = setInterval(async () => {
      // Limita tentativas
      if (pollAttemptsRef.current >= POLL_MAX_TRIES) {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        return;
      }

      pollAttemptsRef.current += 1;

      try {
        // Atualiza a listagem mantendo filtros/página atuais
        await getQueries(pageNumberQueries);
      } catch (e) {
        // Em erro, apenas continua tentando até o limite
        console.error("Polling getQueries error", e);
      }

      // Se após atualizar não há mais "Atividade em execução", encerra
      if (!hasRunningActivity() && pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
        pollAttemptsRef.current = 0;
      }
    }, POLL_INTERVAL_MS);

    // Cleanup defensivo quando filtros/condições mudam
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [hasRunningActivity, pageNumberQueries, POLL_INTERVAL_MS, POLL_MAX_TRIES]);


  return (
    <>
      {/* filtros */}
      <Grid container gap={2} alignItems="spaceAround" textAlign="center">
        {user?.profile === "3" && (
          <FormControl sx={{ marginTop: '9px' }}>
            <Select
              required
              labelId="company-label"
              id="company"
              name="company"
              value={companyIdQueries}
              onChange={handleCompanyChange}
              disabled={loading}
            >
              <MenuItem key={`company-none`} value="0">Selecione a empresa</MenuItem>
              <MenuItem key={`company-all`} value="all">Todas as empresas</MenuItem>
              {companies?.map(c => (
                <MenuItem key={`company-${c.id}`} value={c.id}>
                  {c.corporate_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <InputDatePicker
          value={initialDateQueries}
          setValue={setInitialDateQueries}
        />
        <InputDatePicker
          value={finalDateQueries}
          setValue={setFinalDateQueries}
        />

        <Select
          sx={{ marginTop: '9px' }}
          required
          labelId="results-label"
          id="results"
          name="results"
          value={resultsVisualizations}
          onChange={handleResultsChange}
          disabled={loading}
        >
          <MenuItem key={`results-none`} value={0}>Qtd. Resultados</MenuItem>
          {qtdVisualizations?.map(value => (
            <MenuItem key={`results-${value}`} value={value}>{value}</MenuItem>
          ))}
        </Select>

        <Button
          variant="contained"
          size="large"
          sx={{ marginTop: '9px'}}
          onClick={() => getQueries()}
          disabled={loading}
          color={"success"}
        >
          Buscar
        </Button>

        {queriesSearch!.length > 0 && user?.profile !== "1" && (
          <Button
            variant="outlined"
            size="large"
            sx={{ marginTop: '9px' }}
            onClick={() => startCsvExport()}
            disabled={loading || isCsvExportBusy}
            color={"success"}
          >
            {isCsvExportBusy ? "Gerando CSV" : "Extrair CSV"}
          </Button>
        )}

        {canDownloadCsvExport && (
          <Button
            variant="contained"
            size="large"
            sx={{ marginTop: '9px' }}
            onClick={() => downloadCsvExport()}
            color={"success"}
          >
            Baixar CSV
          </Button>
        )}
      </Grid>

      {showCsvExportProgress && (
        <Alert
          severity={csvExport.phase === "error" ? "error" : csvExport.phase === "completed" ? "success" : "info"}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1, flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {getCsvExportStatusText()}
              </Typography>
              <Typography variant="body2">
                {csvExport.progress_percent}% concluído
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={csvExport.phase === "error" ? 100 : csvExport.progress_percent}
              color={csvExport.phase === "error" ? "error" : "success"}
              sx={{ height: 8, borderRadius: 1 }}
            />

            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
              {csvExport.total_rows > 0
                ? `${formatNumber(csvExport.processed_rows)} de ${formatNumber(csvExport.total_rows)} linhas processadas`
                : `${formatNumber(csvExport.processed_rows)} linhas processadas`}
            </Typography>
          </Box>
        </Alert>
      )}

      <br />

      {/* lista de consultas */}
      <List dense disablePadding sx={{ width: '100%' }}>
        <ListItem key='search' disablePadding>
          <FormControl fullWidth size="medium" variant="standard" sx={{ m: 1 }}>
            <InputLabel htmlFor="input-search">Pesquisar</InputLabel>
            <Input
              id="input-search"
              startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
              onChange={handleChangeSearch}
            />
          </FormControl>
        </ListItem>

        {loading ? (
          <ListItem disablePadding>
            <ListItemAvatar>
              <CircularProgress size={24}/>
            </ListItemAvatar>
            <ListItemText primary="Carregando consultas…" />
          </ListItem>
        ) : queriesSearch!.length > 0 ? (
          queriesSearch?.map((item: QueryProps) => {
            const isErrorStatus = item.status === "4" // Atividade finalizada com erro
            const isDisabled = !isAdmin && isErrorStatus
            const statusLabel =
              item.status === "5"
                ? "Atividade com informações indisponíveis"
                : item.get_status_display

            const rawParams = Object.values(
              (item.data ?? {}) as Record<string, { value: unknown; description?: string; value_type?: string }>
            )

            // pega só valores de texto/número e remove vazios
            const uniqueParamValues = Array.from(
              new Set(
                rawParams
                  .map((p) =>
                    typeof p.value === "string" || typeof p.value === "number"
                      ? String(p.value).trim().slice(0,22)
                      : ""
                  )
                  .filter(Boolean)
              )
            )

            let renderedParams = ""

            if (uniqueParamValues.length === 1) {
              renderedParams = `Parâmetro: ${uniqueParamValues[0]}`
            } else if (uniqueParamValues.length > 1) {
              renderedParams = `Parâmetros: ${uniqueParamValues.join(" | ")}`
            }

            return (
              <ListItem
                key={item.id}
                disablePadding
                sx={width < 1080 ? { flexDirection: "column", alignItems: "stretch" } : undefined}
                secondaryAction={
                  width >= 1080
                    ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={statusLabel}
                          sx={{ backgroundColor: StatusColor[item.status], color: colors.common.white }}
                        />
                        {isAdmin && (
                          <ResponseButton
                            variant="icon"
                            edge="end"
                            getResponses={() => resolveResponsesForQuery(item)}
                          />
                        )}
                      </Box>
                    ) : null
                }
              >
                <ListItemButton
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      navigate(`/query/${item.id}`)
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ backgroundColor: StatusColor[item.status] }}>
                      {item.checkmatch ? <BsUiChecks /> : <ContentPasteSearchIcon />}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      item.products.length > 1
                        ? (
                          <Tooltip
                            title={`${item.products.map((item) => ` ${item}`)}`}
                            arrow
                            placement="right"
                          >
                            <span>Consulta múltipla</span>
                          </Tooltip>
                        )
                        : `${item.products.map((item) => ` ${item}`)}`
                    }
                    secondary={
                      <>
                        {renderedParams}
                        <br />
                        {item.created_at}
                        {width < 1080 && (
                          <>
                            <br />
                            <span style={{ color: StatusColor[item.status] }}>
                              {statusLabel}
                            </span>
                          </>
                        )}
                      </>
                    }
                  />
                </ListItemButton>

                {isAdmin && width < 1080 && (
                  <Box display="flex" justifyContent="flex-end" gap={1} sx={{ px: 2, pb: 1 }}>
                    <ResponseButton variant="icon" getResponses={() => resolveResponsesForQuery(item)} />
                  </Box>
                )}
              </ListItem>
            )
          })
        ) : (
          <ListItem disablePadding>
            <Alert severity="info" variant="filled" sx={{ width: '100%', textAlign: 'center', color: '#ffffff' }}>
              Nada encontrado
            </Alert>
          </ListItem>
        )}
      </List>

      {/* paginação sempre visível */}
      <Paginator
        count={countQueries}
        pageNumber={pageNumberQueries}
        qtdResults={resultsVisualizations}
        onChange={handleChangePage}
      />

    </>
  )
}
